import axios from 'axios';
import { API_BASE_URL } from '../config';

const API = axios.create({ baseURL: API_BASE_URL });

API.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Track logout state to prevent multiple triggers
let _isLoggingOut = false;

API.interceptors.response.use(
  res => res,
  err => {
    const skipLogout = err.config?._skipLogout === true;
    if (err.response?.status === 401 && !skipLogout && !_isLoggingOut) {
      _isLoggingOut = true;
      localStorage.clear();
      setTimeout(() => { window.location.href = '/login'; _isLoggingOut = false; }, 150);
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (data) => API.post('/auth/login', data),
};

export const staffAPI = {
  getAll: () => API.get('/staff'),
  getDirectory: () => API.get('/staff/directory'), // minimal: id, name, designation — no sensitive data
  getById: (id) => API.get(`/staff/${id}`),
  create: (data) => API.post('/staff', data),
  update: (id, data) => API.put(`/staff/${id}`, data),
  deactivate: (id) => API.delete(`/staff/${id}`),
  getKundali: (id) => API.get(`/staff/${id}/employee-full-detail`),
};

const SKIP = { _skipLogout: true };
export const permissionAPI = {
  getAllGroups:    ()              => API.get('/permissions/all'),
  getUsers:       ()              => API.get('/permissions/users'),
  getUser:        (userId)        => API.get(`/permissions/user/${userId}`),
  setUser:        (userId, perms) => API.put(`/permissions/user/${userId}`, { permissions: perms }),
  grant:          (userId, perm)  => API.post(`/permissions/user/${userId}/grant`, { permission: perm }),
  revoke:         (userId, perm)  => API.delete(`/permissions/user/${userId}/revoke`, { data: { permission: perm } }),
  reset:          (userId)        => API.post(`/permissions/user/${userId}/reset`),
};

export const managerAPI = {
  // GET /manager/pending — JWT se auto manager detect, managerId hardcode nahi
  getPending:          ()         => API.get(`/manager/pending`, { ...SKIP }),
  approveLeave:        (id, data) => API.put(`/manager/leave/${id}/approve`, data, { ...SKIP }),
  approveLeaveRequest: (id, data) => API.put(`/manager/leave-request/${id}/approve`, data, { ...SKIP }),
  approveOvertime:     (id, data) => API.put(`/manager/overtime/${id}/approve`, data, { ...SKIP }),
  approveReimbursement:(id, data) => API.put(`/manager/reimbursement/${id}/approve`, data, { ...SKIP }),
  approveLoan:         (id, data) => API.put(`/manager/loan/${id}/approve`, data, { ...SKIP }),
};

export const attendanceAPI = {
  markBulk: (data) => API.post('/attendance/mark', data),
  getToday: () => API.get('/attendance/today'),
  getByDate: (date) => API.get(`/attendance/date/${date}`),
  getMonthly: (staffId, month, year) => API.get(`/attendance/staff/${staffId}/monthly`, { params: { month, year } }),
  getDashboard: () => API.get('/attendance/dashboard'),
  getMonthlySummary: (month, year) => API.get('/attendance/summary', { params: { month, year } }),
};

export const leaveAPI = {
  apply: (data) => API.post('/leaves/apply', data),
  getAll: () => API.get('/leaves'),
  getPending: () => API.get('/leaves/pending'),
  getByStaff: (staffId) => API.get(`/leaves/staff/${staffId}`),
  action: (id, approved, remarks, bypass = {}) => API.put(`/leaves/${id}/action`, { approved, remarks, ...bypass }),
};

export const salaryAPI = {
  generate: (data) => API.post('/salary/generate', data),
  getByMonth: (month, year) => API.get('/salary/month', { params: { month, year } }),
  getSlip: (staffId, month, year) => API.get(`/salary/staff/${staffId}`, { params: { month, year } }),
};

export const holidayAPI = {
  getByYear: (year) => API.get('/holidays', { params: { year } }),
  getUpcoming: () => API.get('/holidays/upcoming'),
  create: (data) => API.post('/holidays', data),
  update: (id, data) => API.put(`/holidays/${id}`, data),
  delete: (id) => API.delete(`/holidays/${id}`),
};

export const announcementAPI = {
  getActive: () => API.get('/announcements/active'),
  getAll: () => API.get('/announcements'),
  create: (data) => API.post('/announcements', data),
  update: (id, data) => API.put(`/announcements/${id}`, data),
  deactivate: (id) => API.patch(`/announcements/${id}/deactivate`),
  delete: (id) => API.delete(`/announcements/${id}`),
};

export const leaveBalanceAPI = {
  getStaff: (staffId, year) => API.get(`/leave-balances/staff/${staffId}`, { params: { year } }),
  initStaff: (staffId, year) => API.post(`/leave-balances/initialize/staff/${staffId}`, null, { params: { year } }),
  initAll: (year) => API.post('/leave-balances/initialize/all', null, { params: { year } }),
  update: (balanceId, totalDays) => API.put(`/leave-balances/${balanceId}`, null, { params: { totalDays } }),
};

export const overtimeAPI = {
  getAll: () => API.get('/overtime'),
  getByStaff: (staffId) => API.get(`/overtime/staff/${staffId}`),
  getByMonth: (month, year) => API.get('/overtime/month', { params: { month, year } }),
  create: (data) => API.post('/overtime', data),
  approve: (id, approved, bypass = {}) => API.put(`/overtime/${id}/action`, { approved, ...bypass }),
};

export const departmentAPI = {
  getAll: () => API.get('/departments'),
  getById: (id) => API.get(`/departments/${id}`),
  create: (data) => API.post('/departments', data),
  update: (id, data) => API.put(`/departments/${id}`, data),
  delete: (id) => API.delete(`/departments/${id}`),
  getStaff: (id) => API.get(`/departments/${id}/staff`),
};

export const companyAPI = {
  get: () => API.get('/company'),
  save: (data) => API.post('/company', data),
  getBranches: () => API.get('/company/branches'),
  createBranch: (data) => API.post('/company/branches', data),
  updateBranch: (id, data) => API.put(`/company/branches/${id}`, data),
  deleteBranch: (id) => API.delete(`/company/branches/${id}`),
};

export const assetAPI = {
  getAll: () => API.get('/assets'),
  getByStaff: (staffId) => API.get(`/assets/staff/${staffId}`),
  assign: (data) => API.post('/assets/assign', data),
  returnAsset: (id, condition, remarks) => API.patch(`/assets/${id}/return`, { condition, remarks }),
};

export const disciplinaryAPI = {
  getAll: () => API.get('/disciplinary'),
  getByStaff: (staffId) => API.get(`/disciplinary/staff/${staffId}`),
  create: (data) => API.post('/disciplinary', data),
  respond: (id, employeeResponse) => API.patch(`/disciplinary/${id}/respond`, { employeeResponse }),
  resolve: (id, resolution) => API.patch(`/disciplinary/${id}/resolve`, { resolution }),
};

export const exitAPI = {
  getAll: () => API.get('/exit'),
  getByStaff: (staffId) => API.get(`/exit/staff/${staffId}`),
  initiate: (data) => API.post('/exit/initiate', data),
  approveFnF: (id) => API.put(`/exit/${id}/approve-fnf`),
  markPaid: (id) => API.patch(`/exit/${id}/mark-paid`),
  addNotes: (id, notes) => API.patch(`/exit/${id}/interview-notes`, { notes }),
};

export const grievanceAPI = {
  getAll: () => API.get('/grievances'),
  getByStaff: (staffId) => API.get(`/grievances/staff/${staffId}`),
  create: (data) => API.post('/grievances', data),
  assign: (id, assignedToUserId) => API.patch(`/grievances/${id}/assign`, { assignedToUserId }),
  resolve: (id, resolution) => API.patch(`/grievances/${id}/resolve`, { resolution }),
  escalate: (id) => API.patch(`/grievances/${id}/escalate`),
};

export const loanAPI = {
  getAll: () => API.get('/loans'),
  getPending: () => API.get('/loans/pending'),
  getByStaff: (staffId) => API.get(`/loans/staff/${staffId}`),
  apply: (data) => API.post('/loans/apply', data),
  action: (id, approved, bypass = {}) => API.put(`/loans/${id}/action`, { approved, ...bypass }),
  processInstallment: (id) => API.patch(`/loans/${id}/process-installment`),
};

export const onboardingAPI = {
  onboard: (data) => API.post('/onboarding', data),
};

export const payGradeAPI = {
  getAll: () => API.get('/pay-grades'),
  create: (data) => API.post('/pay-grades', data),
  update: (id, data) => API.put(`/pay-grades/${id}`, data),
  delete: (id) => API.delete(`/pay-grades/${id}`),
  applyToStaff: (staffId, gradeId) => API.post('/pay-grades/apply', { staffId, gradeId }),
};

export const performanceAPI = {
  getAll: () => API.get('/performance'),
  getByStaff: (staffId) => API.get(`/performance/staff/${staffId}`),
  getByYear: (year) => API.get(`/performance/year/${year}`),
  create: (data) => API.post('/performance', data),
  update: (id, data) => API.put(`/performance/${id}`, data),
  acknowledge: (id) => API.patch(`/performance/${id}/acknowledge`),
};

export const policyAPI = {
  getActive: () => API.get('/policies'),
  getByCategory: (category) => API.get(`/policies/category/${category}`),
  create: (data) => API.post('/policies', data),
  update: (id, data) => API.put(`/policies/${id}`, data),
  deactivate: (id) => API.patch(`/policies/${id}/deactivate`),
};

export const recruitmentAPI = {
  getPostings: () => API.get('/recruitment/postings'),
  createPosting: (data) => API.post('/recruitment/postings', data),
  updatePosting: (id, data) => API.put(`/recruitment/postings/${id}`, data),
  closePosting: (id) => API.patch(`/recruitment/postings/${id}/close`),
  getCandidates: (jobPostingId) => API.get(`/recruitment/postings/${jobPostingId}/candidates`),
  applyCandidate: (data) => API.post('/recruitment/candidates/apply', data),
  updateCandidateStatus: (id, status, notes) => API.patch(`/recruitment/candidates/${id}/status`, { status, notes }),
  makeOffer: (id, data) => API.patch(`/recruitment/candidates/${id}/offer`, data),
};

export const salaryRevisionAPI = {
  getAll: () => API.get('/salary-revisions'),
  getByStaff: (staffId) => API.get(`/salary-revisions/staff/${staffId}`),
  getLatest: (staffId) => API.get(`/salary-revisions/staff/${staffId}/latest`),
  revise: (data) => API.post('/salary-revisions', data),
};

export const shiftAPI = {
  getAll: () => API.get('/shifts'),
  create: (data) => API.post('/shifts', data),
  update: (id, data) => API.put(`/shifts/${id}`, data),
  assign: (data) => API.post('/shifts/assign', data),
  getCurrentShift: (staffId) => API.get(`/shifts/staff/${staffId}/current`),
  getStaffByShift: (shiftId) => API.get(`/shifts/${shiftId}/staff`),
};

export const trainingAPI = {
  getPrograms: () => API.get('/training/programs'),
  createProgram: (data) => API.post('/training/programs', data),
  updateProgram: (id, data) => API.put(`/training/programs/${id}`, data),
  enroll: (staffId, programId) => API.post('/training/enroll', { staffId, programId }),
  markAttendance: (id, status) => API.patch(`/training/enrollments/${id}/attendance`, { status }),
  completeCertification: (id, data) => API.patch(`/training/enrollments/${id}/certification`, data),
  getEmployeeTrainings: (staffId) => API.get(`/training/staff/${staffId}`),
};

export const insuranceAPI = {
  getAll: () => API.get('/insurance'),
  getByStaff: (staffId) => API.get(`/insurance/staff/${staffId}`),
  getAssigned: () => API.get('/insurance/assigned'),
  getUnassigned: () => API.get('/insurance/unassigned'),
  getExpiring: (days = 30) => API.get('/insurance/expiring', { params: { days } }),
  getByStatus: (status) => API.get(`/insurance/status/${status}`),
  create: (data) => API.post('/insurance', data),
  update: (id, data) => API.put(`/insurance/${id}`, data),
  delete: (id) => API.delete(`/insurance/${id}`),
};

export const transferAPI = {
  getAll: () => API.get('/transfers'),
  getByStaff: (staffId) => API.get(`/transfers/staff/${staffId}`),
  getPending: () => API.get('/transfers/pending'),
  getByBranch: (branchId) => API.get(`/transfers/branch/${branchId}`),
  create: (data) => API.post('/transfers', data),
  approve: (id, approvedBy) => API.put(`/transfers/${id}/approve`, null, { params: { approvedBy } }),
  reject: (id, reason) => API.put(`/transfers/${id}/reject`, { reason }),
  delete: (id) => API.delete(`/transfers/${id}`),
};

export const helpdeskAPI = {
  getAll: () => API.get('/helpdesk'),
  getByStaff: (staffId) => API.get(`/helpdesk/staff/${staffId}`),
  getByStatus: (status) => API.get(`/helpdesk/status/${status}`),
  create: (data) => API.post('/helpdesk', data),
  respond: (id, response, resolvedBy) => API.put(`/helpdesk/${id}/respond`, { response, resolvedBy }),
  updateStatus: (id, status) => API.put(`/helpdesk/${id}/status`, { status }),
  delete: (id) => API.delete(`/helpdesk/${id}`),
};

export const hrReportAPI = {
  offerLetter: (staffId, offerDate, joiningDeadline) =>
    API.get(`/hr-reports/offer-letter/${staffId}`, { params: { offerDate, joiningDeadline }, responseType: 'blob' }),
  appointmentLetter: (staffId) =>
    API.get(`/hr-reports/appointment-letter/${staffId}`, { responseType: 'blob' }),
};

export const birthdayAPI = {
  birthdaysToday: () => API.get('/birthday-anniversary/birthdays/today'),
  upcomingBirthdays: (days = 30) => API.get('/birthday-anniversary/birthdays/upcoming', { params: { days } }),
  anniversariesToday: () => API.get('/birthday-anniversary/anniversaries/today'),
  upcomingAnniversaries: (days = 30) => API.get('/birthday-anniversary/anniversaries/upcoming', { params: { days } }),
};

export const reimbursementAPI = {
  getAll: () => API.get('/reimbursements'),
  getByStaff: (staffId) => API.get(`/reimbursements/staff/${staffId}`),
  getByStatus: (status) => API.get(`/reimbursements/status/${status}`),
  create: (data) => API.post('/reimbursements', data),
  approve: (id, approvedAmount, approvedBy, remarks, bypass = {}) => API.put(`/reimbursements/${id}/approve`, { approvedAmount, approvedBy, remarks, ...bypass }),
  reject: (id, remarks, bypass = {}) => API.put(`/reimbursements/${id}/reject`, { remarks, ...bypass }),
  markPaid: (id) => API.put(`/reimbursements/${id}/paid`),
  delete: (id) => API.delete(`/reimbursements/${id}`),
  uploadBill: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/files/upload/bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const statutoryAPI = {
  // ESI
  computeESI: (month, year) => API.post('/statutory/esi/compute', null, { params: { month, year } }),
  getESI: (month, year) => API.get(`/statutory/esi/${month}/${year}`),
  generateESIChallan: (month, year) => API.post('/statutory/esi/challan', null, { params: { month, year } }),
  getESIChallans: (year) => API.get(`/statutory/esi/challans/${year}`),
  updateESIChallanStatus: (id, body) => API.put(`/statutory/esi/challan/${id}/status`, body),
  // PF
  computePF: (month, year) => API.post('/statutory/pf/compute', null, { params: { month, year } }),
  getPF: (month, year) => API.get(`/statutory/pf/${month}/${year}`),
  generatePFChallan: (month, year) => API.post('/statutory/pf/challan', null, { params: { month, year } }),
  getPFChallans: (year) => API.get(`/statutory/pf/challans/${year}`),
  updatePFChallanStatus: (id, body) => API.put(`/statutory/pf/challan/${id}/status`, body),
  // Bonus
  computeBonus: (year, bonusPercent) => API.post('/statutory/bonus/compute', null, { params: { year, bonusPercent } }),
  getBonus: (year) => API.get(`/statutory/bonus/${year}`),
  // Gratuity
  computeGratuity: (staffId, body) => API.post(`/statutory/gratuity/${staffId}`, body),
  getAllGratuity: () => API.get('/statutory/gratuity'),
};

export const professionalTaxAPI = {
  getSlabs: () => API.get('/professional-tax/slabs'),
  getSlabsByState: (state) => API.get(`/professional-tax/slabs/${state}`),
  createSlab: (data) => API.post('/professional-tax/slabs', data),
  updateSlab: (id, data) => API.put(`/professional-tax/slabs/${id}`, data),
  deleteSlab: (id) => API.delete(`/professional-tax/slabs/${id}`),
  compute: (state, grossSalary) => API.get('/professional-tax/compute', { params: { state, grossSalary } }),
  getLWF: () => API.get('/professional-tax/lwf'),
  createLWF: (data) => API.post('/professional-tax/lwf', data),
  updateLWF: (id, data) => API.put(`/professional-tax/lwf/${id}`, data),
};

export const complianceAPI = {
  dashboard: (month, year) => API.get(`/compliance/dashboard/${month}/${year}`),
  esiReport: (month, year) => API.get(`/compliance/esi/report/${month}/${year}`),
  pfReport: (month, year) => API.get(`/compliance/pf/report/${month}/${year}`),
  tdsReport: (month, year) => API.get(`/compliance/tds/report/${month}/${year}`),
};

export const chatAPI = {
  getConversation: (staffId1, staffId2) => API.get('/chat/conversation', { params: { staffId1, staffId2 } }),
  getGroupMessages: () => API.get('/chat/group'),
  getUnreadCount: (staffId) => API.get(`/chat/unread/${staffId}`),
  send: (senderId, receiverId, message, messageType = 'TEXT') => API.post('/chat/send', { senderId, receiverId, message, messageType }),
  markRead: (id) => API.put(`/chat/${id}/read`),
};

export const notificationAPI = {
  getForStaff: (staffId) => API.get(`/notifications/staff/${staffId}`),
  getUnreadCount: (staffId) => API.get(`/notifications/staff/${staffId}/unread-count`),
  markAllRead: (staffId) => API.put(`/notifications/staff/${staffId}/mark-all-read`),
  markRead: (id) => API.put(`/notifications/${id}/read`),
  send: (staffId, title, message, type = 'GENERAL') => API.post('/notifications/send', { staffId, title, message, type }),
  broadcast: (title, message, type = 'GENERAL') => API.post('/notifications/broadcast', { title, message, type }),
  delete: (id) => API.delete(`/notifications/${id}`),
};

export const advancedReportsAPI = {
  monthlyPayroll: (month, year) => API.get('/reports/payroll/monthly', { params: { month, year } }),
  deptWiseSalary: (month, year) => API.get('/reports/payroll/department-wise', { params: { month, year } }),
  attendanceMonthly: (month, year) => API.get('/reports/attendance/monthly', { params: { month, year } }),
  leaveSummary: () => API.get('/reports/leave/summary'),
  hrMis: () => API.get('/reports/hr-mis'),
  insuranceReport: () => API.get('/reports/insurance'),
  transferReport: () => API.get('/reports/transfers'),
  exitReport: () => API.get('/reports/exits'),
};

// Legacy named exports for backward compatibility
export const login = (data) => authAPI.login(data);
export const getStaff = () => staffAPI.getAll();
export const getActiveStaff = () => staffAPI.getAll();
export const createStaff = (data) => staffAPI.create(data);
export const updateStaff = (id, data) => staffAPI.update(id, data);
export const deleteStaff = (id) => staffAPI.deactivate(id);
export const getTodayAttendance = () => attendanceAPI.getToday();
export const getAttendanceByDate = (date) => attendanceAPI.getByDate(date);
export const markAttendance = (data) => attendanceAPI.markBulk(data);
export const getStaffMonthly = (staffId, month, year) => attendanceAPI.getMonthly(staffId, month, year);
export const getAllLeaves = () => leaveAPI.getAll();
export const getPendingLeaves = () => leaveAPI.getPending();
export const getLeavesByStaff = (staffId) => leaveAPI.getByStaff(staffId);
export const applyLeave = (data) => leaveAPI.apply(data);
export const processLeave = (id, { action, remarks }) => leaveAPI.action(id, action === 'APPROVE', remarks);
export const getSalarySlips = (month, year) => salaryAPI.getByMonth(month, year);
export const generateSalary = (data) => salaryAPI.generate(data);

export default API;
